// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DownloadFooter } from '@/components/download-footer';
import { FooterContainer } from '@/components/footer-container';
import { formatNumber } from '@/utilities/number';
import { PluralizeOptions, pluralize } from '@/utilities/pluralize';
import {
  Box,
  Pagination,
  PaginationItem,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';

interface PaginationOptions {
  /**
   * Current page number (1-indexed).
   */
  page: number;
  /**
   * Number of items per page.
   */
  pageSize: number;
  /**
   * Callback when page changes.
   */
  onPageChange: (page: number) => void;
}

interface Props {
  /**
   * Optional pagination configuration. Omit this when the footer should only
   * show export and count information.
   */
  pagination?: PaginationOptions;
  /**
   * Total number of items across all pages.
   * Use null while the first response is loading so the footer can render
   * stable placeholders instead of fabricating a "0 total" state.
   */
  totalCount: number | null;
  /**
   * Options for pluralizing the entity name (e.g., { singular: 'item' }).
   */
  entityOptions: PluralizeOptions;
  /**
   * Optional download URL for export functionality.
   */
  downloadURL?: URL;
  /**
   * Optional label for the download button.
   */
  downloadLabel?: string;
}

const PAGINATION_SKELETON_WIDTH_PX = 240;
const PAGINATION_SKELETON_HEIGHT_PX = 28;
const COUNT_SKELETON_WIDTH_PX = 160;

/**
 * Unified pagination footer component for tables.
 * Combines page navigation, row count indicator, and optional download button.
 *
 * Layout:
 * - Left: Download button (optional)
 * - Center: Page navigation
 * - Right: Row count indicator (e.g., "Items 1-50 (100 total)")
 */
export function TablePaginationFooter(props: Props) {
  const { pagination, totalCount, entityOptions, downloadURL, downloadLabel } =
    props;

  const theme = useTheme();

  const totalPages =
    pagination === undefined || totalCount === null
      ? null
      : Math.max(1, Math.ceil(totalCount / pagination.pageSize));

  // Floor at 1 so an empty table still shows a "1" page stub. MUI's
  // Pagination renders nothing when count is 0, which leaves the footer
  // visually hollow on empty result sets.
  //
  // TODO(#15947): when the URL carries `page > totalPages`
  // the footer should clear the page param so the user lands on a
  // populated page. See ai/todo/pagination-footer-overshoot-recovery.md.
  // Deletes are rare in this app so the regression is bounded for now.

  const pageStart =
    pagination === undefined || totalCount === null
      ? null
      : Math.min((pagination.page - 1) * pagination.pageSize + 1, totalCount);
  const pageEnd =
    pagination === undefined || totalCount === null
      ? null
      : Math.min(pagination.page * pagination.pageSize, totalCount);

  const entityLabel =
    totalCount === null
      ? null
      : pluralize(totalCount, {
          ...entityOptions,
          includeCount: false,
        });
  const ucEntityLabel =
    entityLabel === null
      ? null
      : entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);
  const countLabel =
    totalCount === null || ucEntityLabel === null
      ? null
      : pagination === undefined
        ? pluralize(totalCount, { ...entityOptions, includeCount: true })
        : `${ucEntityLabel} ${
            pageStart === pageEnd
              ? formatNumber(pageStart)
              : `${formatNumber(pageStart)}–${formatNumber(pageEnd)}`
          } (${formatNumber(totalCount)} total)`;

  return (
    <Box
      display="flex"
      position="sticky"
      bottom={theme.spacing(0)}
      left={0}
      right={0}
      justifyContent="space-between"
      alignItems="center"
      py={2}
      px={1}
    >
      {/* Left: Download button (optional) */}
      <Box sx={{ minWidth: 150 }}>
        {downloadURL && downloadLabel && (
          <DownloadFooter
            downloadURL={downloadURL}
            downloadLabel={downloadLabel}
            itemCount={totalCount}
          />
        )}
      </Box>

      {/* Center: Page navigation */}
      <Box>
        {pagination !== undefined && (
          <FooterContainer sx={{ display: 'flex', alignItems: 'center' }}>
            {totalPages === null ? (
              <Skeleton
                variant="rounded"
                width={PAGINATION_SKELETON_WIDTH_PX}
                height={PAGINATION_SKELETON_HEIGHT_PX}
              />
            ) : (
              <Pagination
                size="small"
                page={pagination.page}
                count={totalPages}
                showFirstButton
                showLastButton
                // Disable the whole control when there are no rows so the
                // floored "1" page stub renders as visible-but-inert
                // instead of a clickable button that goes nowhere.
                disabled={totalCount === 0}
                onChange={(_e, newPage) => pagination.onPageChange(newPage)}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    page={
                      typeof item.page === 'number'
                        ? formatNumber(item.page)
                        : item.page
                    }
                  />
                )}
              />
            )}
          </FooterContainer>
        )}
      </Box>

      {/* Right: Row count indicator */}
      <Box sx={{ minWidth: 150, display: 'flex', justifyContent: 'flex-end' }}>
        <FooterContainer sx={{ display: 'flex', alignItems: 'center' }}>
          {countLabel === null ? (
            <Skeleton variant="text" width={COUNT_SKELETON_WIDTH_PX} />
          ) : (
            <Typography variant="subtitle2" lineHeight="26px">
              {countLabel}
            </Typography>
          )}
        </FooterContainer>
      </Box>
    </Box>
  );
}
